import React from 'react';
import Svg, { Circle } from 'react-native-svg';
import { ColorValue, StyleProp, ViewStyle } from 'react-native';
import { createStyles } from '@/contexts/theme-context';

export interface CircularProgressProps {
  /**
   * Color of the unfilled/background arc
   * @default 'transparent'
   */
  backgroundColor?: ColorValue;
  /**
   * Color of the filled progress arc
   * @default '#000000'
   */
  color?: ColorValue;
  /**
   * Progress value from 0 to 100
   */
  progress: number;
  /**
   * Size of the circle (diameter)
   * @default 24
   */
  size?: number;
  /**
   * Starting angle in degrees (0 = top, 90 = right, 180 = bottom, 270 = left)
   * @default -90 (starts at top)
   */
  startAngle?: number;
  /**
   * Width of the stroke
   * @default 3
   */
  strokeWidth?: number;
  /**
   * Additional style for the container
   */
  style?: StyleProp<ViewStyle>;
}

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  size = 24,
  strokeWidth = 3,
  color,
  backgroundColor = 'transparent',
  startAngle = -90,
  style,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, themes] = useStyles();
  const circleColor = color ?? themes.colors.black;
  const clampedProgress = Math.max(0, Math.min(100, progress));

  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const strokeDasharray = circumference;
  const strokeDashoffset =
    circumference - (clampedProgress / 100) * circumference;

  return (
    <Svg
      height={size}
      style={style}
      viewBox={`0 0 ${size} ${size}`}
      width={size}
    >
      {/* Background circle (unfilled portion) */}
      {backgroundColor === 'transparent' ? null : (
        <Circle
          cx={center}
          cy={center}
          fill="none"
          r={radius}
          stroke={backgroundColor}
          strokeWidth={strokeWidth}
        />
      )}
      {/* Progress circle (filled portion) */}
      <Circle
        cx={center}
        cy={center}
        fill="none"
        r={radius}
        stroke={circleColor}
        strokeDasharray={strokeDasharray}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        strokeWidth={strokeWidth}
        transform={`rotate(${startAngle} ${center} ${center})`}
      />
    </Svg>
  );
};
const useStyles = createStyles(() => ({}));
